import { GRID_COLUMNS } from './defaults';

export const MENU_LAYOUT_COLUMNS = GRID_COLUMNS;
/** Lignes disponibles dans la barre menu (disposition libre). */
export const MENU_LAYOUT_ROWS = 3;
export const MENU_ROW_MIN_PX = 34;

export function menuTabKey(entry) {
  if (!entry) return '';
  return `${entry.kind}:${entry.id}`;
}

function clampColStart(start, span) {
  const s = Math.max(1, Math.min(MENU_LAYOUT_COLUMNS, Number(start) || 1));
  const sp = Math.max(1, Math.min(MENU_LAYOUT_COLUMNS, Number(span) || 6));
  return Math.min(s, MENU_LAYOUT_COLUMNS - sp + 1);
}

function clampColSpan(span) {
  return Math.max(1, Math.min(MENU_LAYOUT_COLUMNS, Number(span) || 6));
}

function clampRowStart(start, span) {
  const s = Math.max(1, Math.min(MENU_LAYOUT_ROWS, Number(start) || 1));
  const sp = Math.max(1, Math.min(MENU_LAYOUT_ROWS, Number(span) || 1));
  return Math.min(s, MENU_LAYOUT_ROWS - sp + 1);
}

function clampRowSpan(span) {
  return Math.max(1, Math.min(MENU_LAYOUT_ROWS, Number(span) || 1));
}

export function normalizeTabPlacement(raw) {
  if (!raw || typeof raw !== 'object') {
    return { colStart: 1, colSpan: 8, rowStart: 1, rowSpan: 1, align: 'start' };
  }
  const colSpan = clampColSpan(raw.colSpan);
  const colStart = clampColStart(raw.colStart, colSpan);
  const rowSpan = clampRowSpan(raw.rowSpan);
  const rowStart = clampRowStart(raw.rowStart, rowSpan);
  const align = raw.align === 'center' || raw.align === 'end' ? raw.align : 'start';
  return { colStart, colSpan, rowStart, rowSpan, align };
}

export function resolveTabPlacement(key, index, total, menuProps) {
  const map = menuProps?.tabPlacements || {};
  if (map[key]) return normalizeTabPlacement(map[key]);
  const barRight = menuProps?.tabAppearances?.[key]?.barAlign === 'right';
  const span = 7;
  if (barRight) {
    const colStart = Math.max(1, MENU_LAYOUT_COLUMNS - span + 1 - index * 0);
    return { colStart, colSpan: span, rowStart: 1, rowSpan: 1, align: 'end' };
  }
  const colStart = Math.max(1, MENU_LAYOUT_COLUMNS - (total - index) * span);
  return { colStart, colSpan: span, align: 'end' };
}

export function resolveLogoMenuPlacement(menuProps) {
  const raw = menuProps?.logoMenuPlacement;
  if (raw?.colStart != null) {
    return normalizeTabPlacement({ ...raw, colSpan: raw.colSpan ?? 10 });
  }
  const pos = menuProps?.logoPosition || 'left';
  if (pos === 'center') return { colStart: 20, colSpan: 10, align: 'center' };
  if (pos === 'right') return { colStart: 38, colSpan: 10, align: 'end' };
  return { colStart: 1, colSpan: 10, align: 'start' };
}

/** Positions par défaut (réparties à droite) quand on active la disposition libre. */
export function buildDefaultTabPlacements(entries) {
  const out = {};
  const list = entries || [];
  list.forEach((entry, i) => {
    out[menuTabKey(entry)] = resolveTabPlacement(menuTabKey(entry), i, list.length, {});
  });
  return out;
}

export function menuGridItemStyle(placement) {
  const p = normalizeTabPlacement(placement);
  return {
    gridColumn: `${p.colStart} / span ${p.colSpan}`,
    gridRow: `${p.rowStart} / span ${p.rowSpan}`,
    justifySelf: p.align,
    alignSelf: 'center',
  };
}

/** @deprecated alias */
export function menuGridColumnStyle(placement) {
  return menuGridItemStyle(placement);
}

export function menuLayoutRowCount(menuProps, tabs, { editMode = false } = {}) {
  if (editMode) return MENU_LAYOUT_ROWS;
  let maxRow = 1;
  const bump = (raw) => {
    const p = normalizeTabPlacement(raw);
    maxRow = Math.max(maxRow, p.rowStart + p.rowSpan - 1);
  };
  bump(resolveLogoMenuPlacement(menuProps));
  (tabs || []).forEach((tab, index) => {
    const key = `${tab.kind}:${tab.id}`;
    bump(resolveTabPlacement(key, index, tabs.length, menuProps));
  });
  return maxRow;
}

/** Colonne de départ (1-based) à partir d’un pointeur sur la barre menu. */
export function menuColStartFromPointer(clientX, gridEl, colSpan, grabOffsetCol = 0) {
  if (!gridEl || !gridEl.getBoundingClientRect) return 1;
  const rect = gridEl.getBoundingClientRect();
  if (rect.width <= 0) return 1;
  const pointerCol = Math.min(
    MENU_LAYOUT_COLUMNS,
    Math.max(1, Math.floor(((clientX - rect.left) / rect.width) * MENU_LAYOUT_COLUMNS) + 1),
  );
  const span = clampColSpan(colSpan);
  const start = pointerCol - (Number(grabOffsetCol) || 0);
  return clampColStart(start, span);
}

export function menuRowStartFromPointer(clientY, gridEl, rowSpan, grabOffsetRow = 0) {
  if (!gridEl?.getBoundingClientRect) return 1;
  const rect = gridEl.getBoundingClientRect();
  if (rect.height <= 0) return 1;
  const pointerRow = Math.min(
    MENU_LAYOUT_ROWS,
    Math.max(1, Math.floor(((clientY - rect.top) / rect.height) * MENU_LAYOUT_ROWS) + 1),
  );
  const span = clampRowSpan(rowSpan);
  const start = pointerRow - (Number(grabOffsetRow) || 0);
  return clampRowStart(start, span);
}
